/**
 * People hooks. Loops are computed client-side: the open-loops view query
 * is just `/tasks?person_id=:id&archived=false` filtered by status.
 *
 * "They owe you" vs "you owe them" semantics per the schema doc:
 *   - they owe you: person_id = X AND owner_id != currentUser
 *   - you owe them: person_id = X AND owner_id = currentUser
 *
 * Until /me lands (so we know currentUser.id), the detail screen treats
 * every linked task as "open loop" and surfaces the count without splitting.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend } from './client';
import type { TaskDto } from './tasks';

export interface PersonDto {
  id: string;
  workspaceId: string;
  name: string;
  initials: string;
  contextId: string | null;
  role: string | null;
  color: string;
  lastSeenAt: string | null;
  nextMeetingAt: string | null;
  archivedAt: string | null;
  createdBy: string;
  createdAt: string;
}

export function usePeople(archived = false) {
  return useQuery({
    queryKey: ['people', { archived }],
    queryFn: () =>
      apiGet<{ people: PersonDto[] }>(`/people${archived ? '?archived=true' : ''}`),
    staleTime: 30_000,
  });
}

export function usePerson(id: string | undefined) {
  return useQuery({
    queryKey: ['person', id],
    enabled: !!id,
    queryFn: () => apiGet<{ person: PersonDto }>(`/people/${id}`),
    staleTime: 30_000,
  });
}

export function usePersonTasks(id: string | undefined) {
  return useQuery({
    queryKey: ['person', id, 'tasks'],
    enabled: !!id,
    queryFn: () => apiGet<{ tasks: TaskDto[] }>(`/tasks?person_id=${encodeURIComponent(id ?? '')}`),
    staleTime: 30_000,
  });
}

export interface PersonInput {
  name: string;
  initials: string;
  color: string;
  contextId?: string | null;
  role?: string | null;
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PersonInput) =>
      apiSend<{ person: PersonDto }>('POST', '/people', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export function useUpdatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: Partial<PersonInput> }) =>
      apiSend<{ person: PersonDto }>('PATCH', `/people/${vars.id}`, vars.input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['people'] });
      qc.invalidateQueries({ queryKey: ['person', vars.id] });
    },
  });
}

export function useArchivePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ person: PersonDto }>('POST', `/people/${id}/archive`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['people'] });
      qc.invalidateQueries({ queryKey: ['person', id] });
    },
  });
}

export function useRestorePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ person: PersonDto }>('POST', `/people/${id}/restore`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['people'] });
      qc.invalidateQueries({ queryKey: ['person', id] });
    },
  });
}

// Stable palette for auto-generated avatar colours. Picks deterministically
// from a person's display name so the same person always gets the same colour
// across sessions.
const AVATAR_PALETTE = [
  '#b8714a', '#5a7a4a', '#4a6b8a', '#a8843a', '#8a5a4a',
  '#6a8a5a', '#7a5a8a', '#b8588a', '#a07a3a', '#6a4a6a',
];
export function colorFromName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]!;
}
export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
